import type { OperationState } from "../domain";
import { bootstrapSecretLease, type BootstrapSecretLease } from "./bootstrapSecret";
import type {
  ActivationPreview,
  ActivationRedemption,
  BoardEditRequest,
  CommandRequest,
  ForgeRemoteApi,
  ManagerMessageRequest,
  OperationReceipt,
  ProjectionEnvelope,
  ProjectionStreamOptions,
} from "./contracts";
import {
  parseActivationPreview,
  parseActivationRedemption,
  parseProjectionEnvelope,
  PortalApiError,
} from "./portalContract";
import { readServerSentEvents } from "./sse";

export { parseProjectionEnvelope, PortalApiError } from "./portalContract";

const PORTAL_API_BASE = "/api/forge-remote/v1/";
const CSRF_COOKIE_NAME = "forge_remote_csrf";
const CSRF_HEADER_NAME = "X-Forge-Remote-CSRF";
const ACTIVATION_LOCK_NAME = "lani-forge-remote:activation";
const operationStates = new Set<OperationState>([
  "requested",
  "forwarded",
  "authority-accepted",
  "effective",
  "rejected",
  "timed-out",
  "outcome-unknown",
]);

interface ActivationLockManager {
  request<T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => Promise<T>,
  ): Promise<T>;
}

const fallbackActivationTails = new Map<string, Promise<unknown>>();

interface PortalApiOptions {
  origin?: string;
  fetch?: typeof fetch;
  bootstrapSecret?: BootstrapSecretLease;
  readCookie?: () => string;
  activationLockManager?: ActivationLockManager | null;
}

function browserActivationLockManager(): ActivationLockManager | null {
  if (typeof navigator === "undefined" || !navigator.locks) return null;
  const locks = navigator.locks;
  return {
    request: (name, options, callback) => locks.request(name, options, callback),
  };
}

function runWithFallbackActivationLock<T>(origin: string, callback: () => Promise<T>): Promise<T> {
  const previous = fallbackActivationTails.get(origin) ?? Promise.resolve();
  const pending = previous.catch(() => undefined).then(callback);
  fallbackActivationTails.set(origin, pending);
  return pending.finally(() => {
    if (fallbackActivationTails.get(origin) === pending) fallbackActivationTails.delete(origin);
  });
}

export function readCookieValue(cookie: string, name: string) {
  const prefix = `${name}=`;
  const part = cookie.split(";").map((item) => item.trim()).find((item) => item.startsWith(prefix));
  if (!part) return null;
  const value = part.slice(prefix.length);
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function parseOperationReceipt(value: unknown): OperationReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new PortalApiError("Operation receipt did not match the Forge Remote contract.");
  }
  const receipt = value as Record<string, unknown>;
  const state = receipt.state as OperationState;
  if (receipt.schemaVersion !== "forge-remote.operation-receipt.v1" || !operationStates.has(state)) throw new PortalApiError("Unsupported operation receipt schema or state.");
  if (typeof receipt.operationId !== "string" || receipt.operationId.length === 0
      || typeof receipt.observedAt !== "string" || !Number.isFinite(Date.parse(receipt.observedAt))) {
    throw new PortalApiError("Operation receipt did not match the Forge Remote contract.");
  }
  return {
    schemaVersion: "forge-remote.operation-receipt.v1",
    operationId: receipt.operationId,
    state,
    observedAt: receipt.observedAt,
    authorityOperationId: typeof receipt.authorityOperationId === "string" ? receipt.authorityOperationId : undefined,
    effectiveEventId: typeof receipt.effectiveEventId === "string" ? receipt.effectiveEventId : undefined,
    detail: typeof receipt.detail === "string" ? receipt.detail : undefined,
  };
}

export class PortalForgeRemoteApi implements ForgeRemoteApi {
  readonly sourceKind = "portal" as const;
  readonly supportsProjectionStream = true;
  readonly #origin: string;
  readonly #fetch: typeof fetch;
  readonly #bootstrapSecret: BootstrapSecretLease;
  readonly #readCookie: () => string;
  readonly #activationLockManager: ActivationLockManager | null;

  constructor(options: PortalApiOptions = {}) {
    const runtimeOrigin = options.origin ?? (typeof window === "undefined" ? "http://localhost" : window.location.origin);
    this.#origin = new URL(runtimeOrigin).origin;
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#bootstrapSecret = options.bootstrapSecret ?? bootstrapSecretLease;
    this.#readCookie = options.readCookie ?? (() => typeof document === "undefined" ? "" : document.cookie);
    this.#activationLockManager = options.activationLockManager === undefined
      ? browserActivationLockManager()
      : options.activationLockManager;
  }

  hasCommandProtection() {
    return this.#csrfToken() !== null;
  }

  async getActivationPreview(): Promise<ActivationPreview> {
    return this.#withActivationLock(async () => parseActivationPreview(
      await this.#postWithBootstrapSecret("activation/preview", "forge-remote.activation-preview-request.v1"),
    ));
  }

  async redeemActivation(): Promise<ActivationRedemption> {
    return this.#withActivationLock(async () => {
      const result = parseActivationRedemption(
        await this.#postWithBootstrapSecret("activation/redeem", "forge-remote.activation-preview-request.v1"),
      );
      this.#bootstrapSecret.clear();
      return result;
    });
  }

  async getCurrentProjection(): Promise<ProjectionEnvelope> {
    return parseProjectionEnvelope(await this.#requestJson("projection/current", { method: "GET" }));
  }

  async streamProjectionUpdates({ cursor, signal, onProjection, onActivity }: ProjectionStreamOptions): Promise<void> {
    const headers = new Headers({ Accept: "text/event-stream", "Cache-Control": "no-store" });
    if (cursor) headers.set("Last-Event-ID", cursor);
    const response = await this.#fetch(this.#endpoint("projection/updates"), {
      method: "GET",
      headers,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal,
    });
    if (!response.ok) throw new PortalApiError(`Projection update stream was rejected (${response.status}).`, response.status);
    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    if (!contentType.includes("text/event-stream") || !response.body) throw new PortalApiError("Projection update stream returned an unsupported response.");
    onActivity?.();

    await readServerSentEvents(response.body, (event) => {
      if (event.event && event.event !== "projection") return;
      const envelope = parseProjectionEnvelope(JSON.parse(event.data) as unknown);
      if (event.id && event.id !== envelope.cursor) throw new PortalApiError("Projection stream cursor mismatch.");
      onProjection(envelope);
    }, onActivity);
  }

  async endSession(): Promise<void> {
    const csrf = this.#csrfToken();
    if (!csrf) throw new PortalApiError("Forge Remote session command protection is unavailable.");
    const response = await this.#fetch(this.#endpoint("session/end"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store",
        "Content-Type": "application/json",
        [CSRF_HEADER_NAME]: csrf,
      },
      body: JSON.stringify({ schemaVersion: "forge-remote.session-end-command.v1" }),
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok && response.status !== 401) {
      throw new PortalApiError(`Forge Remote session end was rejected (${response.status}).`, response.status);
    }
  }

  async sendManagerMessage(request: ManagerMessageRequest): Promise<OperationReceipt> {
    return parseOperationReceipt(await this.#requestSessionJson("manager/messages", {
      method: "POST",
      body: JSON.stringify({ schemaVersion: "forge-remote.manager-message-command.v1", ...request }),
    }));
  }

  async submitCommand(request: CommandRequest): Promise<OperationReceipt> {
    return parseOperationReceipt(await this.#requestSessionJson("commands", {
      method: "POST",
      body: JSON.stringify({ schemaVersion: "forge-remote.command.v1", ...request }),
    }));
  }

  async editBoardItem(request: BoardEditRequest): Promise<OperationReceipt> {
    return parseOperationReceipt(await this.#requestSessionJson("board/commands", {
      method: "POST",
      body: JSON.stringify({ schemaVersion: "forge-remote.board-command.v2", ...request }),
    }));
  }

  #postWithBootstrapSecret(path: string, schemaVersion: string) {
    return this.#bootstrapSecret.use((bootstrapSecret) => this.#requestJson(path, {
      method: "POST",
      body: JSON.stringify({ schemaVersion, bootstrapSecret }),
    }));
  }

  #withActivationLock<T>(callback: () => Promise<T>): Promise<T> {
    return this.#activationLockManager
      ? this.#activationLockManager.request(ACTIVATION_LOCK_NAME, { mode: "exclusive" }, callback)
      : runWithFallbackActivationLock(this.#origin, callback);
  }

  #endpoint(path: string) {
    const endpoint = new URL(path, new URL(PORTAL_API_BASE, this.#origin));
    if (endpoint.origin !== this.#origin) throw new PortalApiError("Forge Remote Portal endpoint must remain same-origin.");
    return endpoint.href;
  }

  #csrfToken() {
    return readCookieValue(this.#readCookie(), CSRF_COOKIE_NAME);
  }

  #requestSessionJson(path: string, init: RequestInit) {
    const csrf = this.#csrfToken();
    if (!csrf) throw new PortalApiError("Forge Remote session command protection is unavailable.");
    const headers = new Headers(init.headers);
    headers.set(CSRF_HEADER_NAME, csrf);
    return this.#requestJson(path, { ...init, headers });
  }

  async #requestJson(path: string, init: RequestInit) {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    headers.set("Cache-Control", "no-store");
    if (init.body) headers.set("Content-Type", "application/json");
    const response = await this.#fetch(this.#endpoint(path), {
      ...init,
      headers,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) throw new PortalApiError(`Forge Remote Portal request was rejected (${response.status}).`, response.status);
    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    if (!contentType.includes("application/json")) throw new PortalApiError("Forge Remote Portal returned an unsupported response.");
    return response.json() as Promise<unknown>;
  }
}
